<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop, Ref } from "@/helpers/Decorators";
import { Form, FormType } from "@/helpers/Form";
import IdeoModal from "@/components/ideo/modal/IdeoModal.vue";
import DocumentRelationshipsService, { CreateModel } from "@/modules/low-code/services/DocumentRelationshipsService";
import { Contractor } from '@/modules/studio/forms/services/FormsService';

@Options({
    components: {},
    emits: ['reload']
})
export default class LinkDocumentsModal extends Vue
{
    @Prop()
    public documentId: string;

    @Prop()
    public licence: string;

    @Prop()
    public blueprintId: string;

    @Prop({ default: '' })
    public viewStateKey: string;

    @Prop({ default: '' })
    public emitId: string;

    @Ref('link-documents-modal')
    public modal: () => IdeoModal;

    public get linkDocumentsEventName(): string
    {
        return this.blueprintId ? `link-documents:${this.blueprintId}` : 'link-documents';
    }

    public form: FormType<CreateModel> = Form.create<CreateModel>({
        document: null,
        blueprintId: ''
    });

    public mounted(): void
    {
        this.$events.$on(this.linkDocumentsEventName, () =>
        {
            this.form.clear();
            this.form.blueprintId = this.blueprintId;
            this.modal().show();
        });
    }

    public async submit(): Promise<void>
    {
        try
        {
            this.form.wait();

            await DocumentRelationshipsService.createRelation(this.documentId, this.licence, this.form.formatData());
            this.$alert.success(this.$t('[[[Dokument został powiązany.]]]'));

            if (this.emitId)
                this.$events.$emit(this.emitId, { resetPageIndex: true });
            else if (this.viewStateKey)
                this.$events.$emit(`refetchData:${this.viewStateKey}`);
            else
                this.$events.$emit('refetchData');
            this.modal().hide();
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
                422: (ex: any) => this.form.$errors.record(ex.data.errors)
            });
        }
        finally
        {
            this.form.continue();
        }
    }

    public unmounted(): void
    {
        this.$events.$off(this.linkDocumentsEventName);
    }

    public isContractors(contractor: Array<Contractor>): boolean
    {
        return contractor?.length > 0;
    }
}
</script>

<template>
    <IdeoModal ref="link-documents-modal" :title="$t('[[[Powiąż dokument]]]')" centered>
        <template #default>
            <IdeoFormGroup :label="$t('[[[Dokument]]]')" :invalid-feedback="form.$errors.first('document')" :state="form.$errors.state('document')">
                <IdeoSelect
                    v-model="form.document"
                    :placeholder="$t('[[[Dokument]]]')"
                    :endpoint="`${licence}/${documentId}/related-documents/documents/${blueprintId}`"
                    :multiselect="false"
                    class="flex-fill"
                    @update:modelValue="form.$errors.clear('document')"
                >
                    <template #option="{ option }">
                        <div class="d-flex flex-column">
                            <strong v-if="option.value">{{ option.value }}</strong>
                            <small v-if="isContractors(option.contractor)">
                                {{ $t('[[[Kontrahent]]]') }}: {{ option.contractor.map((item: Contractor) => item.name).join(", ") }}
                            </small>
                            <small v-if="option.module">{{ $t('[[[Typ dokumentu]]]') }}: {{ option.module }}</small>
                        </div>
                    </template>
                </IdeoSelect>
            </IdeoFormGroup>
        </template>
        <template #modal-footer="{ cancel }">
            <IdeoButton variant="success" :disabled="!form.active()" @click="submit">
                {{ $t('[[[Powiąż]]]') }}
            </IdeoButton>
            <IdeoButton variant="secondary" @click="cancel()">
                {{ $t('[[[Anuluj]]]') }}
            </IdeoButton>
        </template>
    </IdeoModal>
</template>
